/**
 * Program Monitor — displays live output, handles take action and monitor controls
 * Supports zone-based rendering: multiple items visible simultaneously in assigned zones.
 */
import { monitorStore } from '../stores/monitorStore';
import { projectStore } from '../stores/projectStore';
import { zoneStore, DESIGN_W, DESIGN_H } from '../stores/zoneStore';
import { initTransitionEngine, executeTake, clearProgram, setZoneRenderer } from '../engine/transitionEngine';
import { renderTemplate, applyContentBoundsZoom } from '../engine/templateRenderer';
import { eventBus } from '../engine/eventBus';
import { broadcastProgramState, broadcastProgramClear, onFocusSim } from '../engine/videoBoardChannel';

let canvasEl: HTMLElement;
let sliderEl: HTMLElement;
let monitorCombo: HTMLElement;
let subtitleEl: HTMLElement;
let videoBoardWindow: Window | null = null;

export function initProgram(): void {
  canvasEl = document.getElementById('program-canvas')!;
  sliderEl = document.querySelector('.m-slider')!;
  monitorCombo = document.querySelector('.monitor-combo')!;
  subtitleEl = document.getElementById('program-subtitle')!;

  // Initialize transition engine with program canvas
  initTransitionEngine(canvasEl);
  setZoneRenderer(renderProgramWithZones);

  // Take button (Space/Enter) — handled by keyboard manager in Step 9
  // For now, also listen to the program bottom bar buttons
  const pgmBtns = document.querySelectorAll<HTMLButtonElement>('.pgm-btn');
  if (pgmBtns[0]) {
    pgmBtns[0].title = 'Stop/Clear Program';
    pgmBtns[0].addEventListener('click', () => clearProgram());
  }
  if (pgmBtns[1]) {
    pgmBtns[1].title = 'Reset Program';
    pgmBtns[1].addEventListener('click', () => clearProgram());
  }

  // Monitor level slider (vertical)
  initMonitorSlider();

  // Monitor combo dropdown
  monitorCombo.addEventListener('click', toggleMonitorDropdown);

  // Re-render program on field changes (live update from Properties panel)
  eventBus.on('properties:fieldChanged', (payload) => {
    const programIds = monitorStore.getState().programItemIds;
    if (programIds.includes(payload.itemId)) {
      renderProgramWithZones();
    }
  });

  // Re-render program on data-linked field changes (live update from ScoreBird etc.)
  eventBus.on('project:itemUpdated', (payload) => {
    const programIds = monitorStore.getState().programItemIds;
    if (programIds.includes(payload.itemId)) {
      renderProgramWithZones();
    }
  });

  // Re-render when zone preset changes
  eventBus.on('zone:presetChanged', () => {
    updateSubtitle();
    applyCanvasAspectRatio();
    const programIds = monitorStore.getState().programItemIds;
    if (programIds.length > 0) {
      renderProgramWithZones();
    }
  });

  // Set initial subtitle and aspect ratio
  updateSubtitle();
  applyCanvasAspectRatio();

  // Re-render when a zone assignment changes for a program item
  eventBus.on('zone:assignmentChanged', (payload) => {
    const programIds = monitorStore.getState().programItemIds;
    if (programIds.includes(payload.itemId)) {
      renderProgramWithZones();
    }
  });

  // Listen for take events
  eventBus.on('monitor:takeToProgram', () => {
    // Mark items as active in project
    const programIds = monitorStore.getState().programItemIds;
    // Update status bar could go here
  });
}

/** Execute a take — called from keyboard shortcuts and other modules */
export function doTake(): void {
  executeTake();
}

/**
 * Render all program items into their assigned zone containers.
 * Items without a zone assignment render into the largest zone.
 * Exported so transitionEngine can call it.
 */
export function renderProgramWithZones(): void {
  const programIds = monitorStore.getState().programItemIds;
  if (programIds.length === 0) {
    canvasEl.innerHTML = '';
    canvasEl.classList.remove('program-active');
    broadcastProgramClear();
    return;
  }

  canvasEl.classList.add('program-active');

  const preset = zoneStore.getState().getActivePreset();

  // Single zone (Full Layout / No Zones) — render all items as stacked layers
  if (preset.zones.length === 1) {
    canvasEl.innerHTML = '';
    for (const itemId of programIds) {
      const wrapper = document.createElement('div');
      wrapper.style.cssText = 'position:absolute;inset:0;';
      canvasEl.appendChild(wrapper);
      renderTemplate(wrapper, itemId);
    }
    broadcastProgramState(canvasEl);
    return;
  }

  // Multi-zone — create zone containers
  const largestZoneId = zoneStore.getState().getLargestZoneId();

  canvasEl.innerHTML = '';
  canvasEl.style.position = 'relative';

  const zoneContainers = new Map<number, HTMLElement>();
  for (const zone of preset.zones) {
    const container = document.createElement('div');
    container.className = 'pgm-zone';
    container.dataset.zoneId = String(zone.id);
    container.style.left = `${(zone.x / DESIGN_W * 100).toFixed(4)}%`;
    container.style.top = `${(zone.y / DESIGN_H * 100).toFixed(4)}%`;
    container.style.width = `${(zone.w / DESIGN_W * 100).toFixed(4)}%`;
    container.style.height = `${(zone.h / DESIGN_H * 100).toFixed(4)}%`;
    canvasEl.appendChild(container);
    zoneContainers.set(zone.id, container);
  }

  // Render each program item into its assigned zone.
  for (const itemId of programIds) {
    const item = projectStore.getState().getItem(itemId);
    if (!item) continue;

    const targetZoneId = item.zoneId ?? largestZoneId;
    const container = zoneContainers.get(targetZoneId);
    if (!container) continue;

    container.innerHTML = '';
    const inner = document.createElement('div');
    inner.style.cssText = 'position:absolute;inset:0;';
    container.appendChild(inner);
    renderTemplate(inner, itemId);

    // Zoom to content bounds when rendering in a zone
    if (item.templateId) {
      applyContentBoundsZoom(inner, item.templateId);
    }
  }

  // Broadcast to video board tab
  broadcastProgramState(canvasEl);
}

function updateSubtitle(): void {
  const preset = zoneStore.getState().getActivePreset();
  const vf = zoneStore.getState().videoFormat;
  const zoneCount = preset.zones.length;
  const zonePart = zoneCount === 1
    ? 'Full Layout'
    : `${preset.name} (${zoneCount} zones)`;
  const scanType = vf.interlaced ? 'Interlaced' : 'Progressive';
  const fpsLabel = Number.isInteger(vf.fps) ? `${vf.fps} fps` : `${vf.fps} fields/sec`;
  subtitleEl.textContent = `\u00A0- [${vf.width} x ${vf.height}, ${fpsLabel}, ${scanType}] ${zonePart}`;
}

/**
 * Apply the video format aspect ratio to both preview and program canvases.
 */
function applyCanvasAspectRatio(): void {
  const vf = zoneStore.getState().videoFormat;
  const ratio = `${vf.width} / ${vf.height}`;
  canvasEl.style.aspectRatio = ratio;
  const previewCanvas = document.getElementById('preview-canvas');
  if (previewCanvas) {
    previewCanvas.style.aspectRatio = ratio;
  }
}

function initMonitorSlider(): void {
  const ticks = document.querySelectorAll<HTMLElement>('.m-tick');
  const state = monitorStore.getState();

  // Highlight current level
  updateSliderVisual(state.monitorLevel);

  ticks.forEach((tick) => {
    tick.style.cursor = 'pointer';
    tick.addEventListener('click', () => {
      const level = parseInt(tick.textContent || '4', 10);
      monitorStore.getState().setMonitorLevel(level);
      updateSliderVisual(level);
    });
  });

  // Slider drag
  sliderEl.style.cursor = 'pointer';
  sliderEl.addEventListener('click', (e) => {
    const rect = sliderEl.getBoundingClientRect();
    const pct = (e.clientY - rect.top) / rect.height;
    const level = Math.round(7 - pct * 6); // 7 at top, 1 at bottom
    monitorStore.getState().setMonitorLevel(Math.max(1, Math.min(7, level)));
    updateSliderVisual(Math.max(1, Math.min(7, level)));
  });
}

function updateSliderVisual(level: number): void {
  // Update the slider thumb position
  const pct = ((7 - level) / 6) * 100;
  sliderEl.style.setProperty('--slider-pos', `${pct}%`);

  // Update tick highlights
  document.querySelectorAll<HTMLElement>('.m-tick').forEach((tick) => {
    const tickLevel = parseInt(tick.textContent || '0', 10);
    tick.style.color = tickLevel <= level ? 'var(--accent-green)' : 'var(--text-dim)';
  });
}

/** Open video board on sim boot — pre-buffers video so there's zero delay */
export function openVideoBoardOnBoot(): void {
  // Seed localStorage with the live camera URL so video board preloads it immediately
  const liveUrl = 'https://switchersimvids.blob.core.windows.net/graphicsim-media/decklink1.webm';
  try {
    localStorage.setItem('vb-video-urls', JSON.stringify([{ src: liveUrl, currentTime: 0 }]));
  } catch (_) {}

  openVideoBoard(false);
  monitorStore.getState().setMonitorEnabled(true);
  if (monitorCombo) {
    monitorCombo.innerHTML = 'DISPLAY <span class="dd">&#9662;</span>';
    monitorCombo.classList.add('active');
  }

  // Listen for "Back to Simulator" requests from the video board tab
  onFocusSim(() => window.focus());
}

function openVideoBoard(requestFullscreen: boolean): void {
  // Close existing window if it's still open
  if (videoBoardWindow && !videoBoardWindow.closed) {
    videoBoardWindow.focus();
    return;
  }

  const url = location.origin + location.pathname + '#/videoboard';
  videoBoardWindow = window.open(url, 'nevco-videoboard');
  eventBus.emit('ui:videoBoardOpened', {});

  if (requestFullscreen && videoBoardWindow) {
    // Request fullscreen after the page loads
    videoBoardWindow.addEventListener('load', () => {
      const container = videoBoardWindow?.document.getElementById('videoboard-container');
      if (container) container.requestFullscreen().catch(() => {});
    });
  }

  // Broadcast current program state so the new tab picks it up immediately
  setTimeout(() => {
    const programIds = monitorStore.getState().programItemIds;
    if (programIds.length > 0) {
      broadcastProgramState(canvasEl);
    }
  }, 500);
}

export function closeVideoBoard(): void {
  if (videoBoardWindow && !videoBoardWindow.closed) {
    videoBoardWindow.close();
  }
  videoBoardWindow = null;
}

function toggleMonitorDropdown(e: Event): void {
  e.stopPropagation();

  // Remove existing dropdown
  const existing = document.querySelector('.monitor-dropdown');
  if (existing) {
    existing.remove();
    return;
  }

  const currentLabel = monitorCombo.textContent?.replace(/\s*▾\s*$/, '').trim() || 'NO DISPLAY';
  const options = ['NO DISPLAY', 'DISPLAY', 'FULLSCREEN'];
  const dropdown = document.createElement('div');
  dropdown.className = 'monitor-dropdown';
  dropdown.innerHTML = options.map((opt) =>
    `<div class="monitor-dropdown-item${opt === currentLabel ? ' active' : ''}">${opt}</div>`
  ).join('');

  // Position above the combo
  const rect = monitorCombo.getBoundingClientRect();
  dropdown.style.cssText = `position:fixed;left:${rect.left}px;bottom:${window.innerHeight - rect.top + 2}px;z-index:1000;`;

  document.body.appendChild(dropdown);

  dropdown.querySelectorAll<HTMLDivElement>('.monitor-dropdown-item').forEach((item) => {
    item.addEventListener('click', () => {
      const text = item.textContent || '';
      monitorCombo.innerHTML = `${text} <span class="dd">&#9662;</span>`;
      dropdown.remove();

      if (text === 'NO DISPLAY') {
        monitorStore.getState().setMonitorEnabled(false);
        monitorCombo.classList.remove('active');
        closeVideoBoard();
      } else {
        monitorStore.getState().setMonitorEnabled(true);
        monitorCombo.classList.add('active');
        openVideoBoard(text === 'FULLSCREEN');
      }
    });
  });

  // Close on outside click
  const close = () => { dropdown.remove(); document.removeEventListener('click', close); };
  setTimeout(() => document.addEventListener('click', close), 0);
}
